import React, { FC, MutableRefObject, useCallback } from 'react';
import { Formik } from 'formik';
import styled from 'styled-components';
import { WindowWidthContext } from '@minecraft.utils';
import { saveFilterUtilities } from '@blocs.filter';
import { useTranslation } from '@blocs.i18n';
import { FilterValues } from '../../shared/types';
import { FormItem, ClearButton } from '../../shared/styles';
import {
  StyledForm,
  FilterOverlayContent,
  FilterOverlayContentHeader,
  FilterOverlayContentBody,
  CloseIcon,
} from './FilterOverlayFormStyles';
import FilterOverlayFormListener from './FilterOverlayFormListener';
import FilterOverlayFormButtonGroup from './FilterOverlayFormButtonGroup';

const StyledClearButton = styled(ClearButton)`
  font-size: 1rem;
  position: absolute;
  right: 0;
  top: 0;
  margin: 0;
`;

type Props = {
  isClearButtonVisible: boolean;
  showAdvancedSearchButton?: boolean;
  advancedSearchRoute?: string;
  searchButtonLabel?: string;
  sessionStorageKey: string;
  formikRef: MutableRefObject<any>;
  retainedFilters: any;
  toggleFilterOverlay: () => void;
  onClear: (filtersKeys?: string[]) => void;
  onRetainedFiltersChange: (newFilters: FilterValues) => void;
  onFiltersChange: (newFilters: FilterValues) => void;
  children: JSX.Element;
};

const FilterOverlayForm: FC<Props> = ({
  isClearButtonVisible,
  showAdvancedSearchButton,
  advancedSearchRoute,
  searchButtonLabel,
  sessionStorageKey,
  formikRef,
  retainedFilters,
  toggleFilterOverlay,
  onClear,
  onRetainedFiltersChange,
  onFiltersChange,
  children,
}) => {
  const { t } = useTranslation();
  const { lessSm } = WindowWidthContext.useWidthData();

  const handleSubmit = useCallback(
    (values): void => {
      saveFilterUtilities.saveFiltersForThisSession({ storageKey: sessionStorageKey, filters: values });
      onFiltersChange(values);

      toggleFilterOverlay();
    },
    [onFiltersChange, sessionStorageKey, toggleFilterOverlay]
  );

  return (
    <FilterOverlayContent>
      {lessSm && (
        <FilterOverlayContentHeader>
          <h3>{t('common:button.filter')}</h3>
          <CloseIcon inheritColor name="close" onClick={toggleFilterOverlay} />
        </FilterOverlayContentHeader>
      )}
      <FilterOverlayContentBody>
        {isClearButtonVisible && (
          <StyledClearButton
            onClick={(): void => {
              onClear();
            }}
          >
            {t('common:button.clear')}
          </StyledClearButton>
        )}
        <Formik innerRef={formikRef} initialValues={retainedFilters} onSubmit={handleSubmit}>
          {(): JSX.Element => (
            <StyledForm>
              <FilterOverlayFormListener onRetainedFiltersChange={onRetainedFiltersChange} />
              {children}
              <FormItem>
                <FilterOverlayFormButtonGroup
                  showAdvancedSearchButton={showAdvancedSearchButton}
                  advancedSearchRoute={advancedSearchRoute}
                  searchButtonLabel={searchButtonLabel}
                />
              </FormItem>
            </StyledForm>
          )}
        </Formik>
      </FilterOverlayContentBody>
    </FilterOverlayContent>
  );
};

export default FilterOverlayForm;
